import { pickLatestCollectionRun } from '../../../utils/collectionRuns';
import { isWorkbenchNonRerunRun } from '../../../utils/workbenchRunFilter';
import { normalizeUploadBaseTitle, resolveUploadRecordId } from '../shared/analysisUploadFilter';

export type AISafetyRun = Record<string, unknown>;
export type AISafetyInputType = 'LLM_APP' | 'ML_APP' | 'BOTH' | 'LEGACY' | 'UNKNOWN';

function getArray(value: unknown): AISafetyRun[] {
  if (Array.isArray(value)) {
    return value.filter((item): item is AISafetyRun => Boolean(item) && typeof item === 'object');
  }
  if (value && typeof value === 'object') {
    return [value as AISafetyRun];
  }
  return [];
}

/** Prefer `ai_safety`; fall back to legacy `abuse_ai` when absent. Merges LLM-specific buckets. */
export function resolveSafetyArrayFromBucket(bucket: Record<string, unknown> | null | undefined): AISafetyRun[] {
  if (!bucket) return [];

  const merged: AISafetyRun[] = [];
  for (const key of ['ai_safety', 'ai_safety_llm', 'abuse_ai', 'abuse_ai_llm'] as const) {
    merged.push(...getArray(bucket[key]));
  }
  return merged;
}

/** Resolve safety runs from project results, optionally merged with 360 custom payload. */
export function collectSafetyRuns(projectResults: unknown, customData?: unknown): AISafetyRun[] {
  const fromProject = gatherSafetyRunsFromProject(projectResults);

  let fromCustom: AISafetyRun[] = [];
  if (customData != null) {
    if (Array.isArray(customData)) {
      fromCustom = getArray(customData).filter(isWorkbenchNonRerunRun);
    } else if (typeof customData === 'object') {
      fromCustom = [customData as AISafetyRun];
    }
  }

  if (fromCustom.length === 0) {
    return dedupeRuns(expandSafetyRunsByEntries(fromProject));
  }

  const merged = dedupeRuns(expandSafetyRunsByEntries([...fromProject, ...fromCustom]));
  return enrichSparseSafetyRuns(merged);
}

function gatherSafetyRunsFromProject(projectResults: unknown): AISafetyRun[] {
  if (!projectResults || typeof projectResults !== 'object') return [];
  const pr = projectResults as Record<string, unknown>;
  const sections: AISafetyRun[] = [];

  for (const rootKey of ['results', 'collection_results', 'feature_results'] as const) {
    const root = pr[rootKey];
    if (!root || typeof root !== 'object') continue;
    sections.push(...resolveSafetyArrayFromBucket(root as Record<string, unknown>).filter(isWorkbenchNonRerunRun));
  }

  if (pr.ai_safety != null) {
    sections.push(...getArray(pr.ai_safety).filter(isWorkbenchNonRerunRun));
  }
  if (pr.ai_safety_llm != null) {
    sections.push(...getArray(pr.ai_safety_llm).filter(isWorkbenchNonRerunRun));
  }
  if (pr.abuse_ai != null) {
    sections.push(...getArray(pr.abuse_ai).filter(isWorkbenchNonRerunRun));
  }
  if (pr.abuse_ai_llm != null) {
    sections.push(...getArray(pr.abuse_ai_llm).filter(isWorkbenchNonRerunRun));
  }

  return sections;
}

function safetyRunRichness(run: AISafetyRun): number {
  let score = 0;
  const analysisType = String(run?.analysis_type ?? run?.analysisType ?? run?.AnalysisType ?? '').trim();
  const inputType = String(run?.input_type ?? run?.inputType ?? '').trim();
  if (analysisType) score += 8;
  if (inputType) score += 4;
  const controlCount = Object.keys(readControls(run)).length;
  if (controlCount > 0) score += 4 + Math.min(controlCount, 4);
  if (run.risk_score != null || run.score_breakdown != null) score += 1;
  return score;
}

function enrichSparseSafetyRuns(runs: AISafetyRun[]): AISafetyRun[] {
  const donors = runs.filter(run => Object.keys(readControls(run)).length > 0);
  if (donors.length === 0) return runs;

  return runs.map(run => {
    if (Object.keys(readControls(run)).length > 0) return run;

    const inputType = resolveInputType(run);
    const donor =
      donors.find(candidate => resolveInputType(candidate) === inputType) ??
      (inputType === 'BOTH' ? donors.find(candidate => resolveInputType(candidate) === 'BOTH') : undefined) ??
      donors[0];

    return mergeSafetyRunPair(donor, run);
  });
}

function mergeControlMaps(
  a: Record<string, Record<string, unknown>>,
  b: Record<string, Record<string, unknown>>
): Record<string, Record<string, unknown>> {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  const merged: Record<string, Record<string, unknown>> = {};

  for (const key of keys) {
    const entryA = a[key];
    const entryB = b[key];
    if (entryA && entryB) {
      const scoreA = controlScore(entryA) ?? -1;
      const scoreB = controlScore(entryB) ?? -1;
      merged[key] = scoreB > scoreA ? entryB : scoreA > scoreB ? entryA : { ...entryA, ...entryB };
    } else {
      merged[key] = (entryB ?? entryA) as Record<string, unknown>;
    }
  }

  return merged;
}

function mergeSafetyRunPair(existing: AISafetyRun, incoming: AISafetyRun): AISafetyRun {
  const keepIncoming = safetyRunRichness(incoming) >= safetyRunRichness(existing);
  const merged = keepIncoming ? { ...existing, ...incoming } : { ...incoming, ...existing };
  const controls = mergeControlMaps(readControls(existing), readControls(incoming));

  if (Object.keys(controls).length === 0) return merged;

  const baseBreakdown =
    merged.score_breakdown && typeof merged.score_breakdown === 'object' && !Array.isArray(merged.score_breakdown)
      ? (merged.score_breakdown as Record<string, unknown>)
      : {};

  return {
    ...merged,
    score_breakdown: {
      ...baseBreakdown,
      controls
    }
  };
}

function dedupeRuns(items: AISafetyRun[]): AISafetyRun[] {
  const byId = new Map<string, AISafetyRun>();
  items.forEach((item, index) => {
    const id = String(item.result_id ?? '') || `${String(item.title ?? 'safety')}-${String(item.timestamp ?? index)}`;
    const normalized = { ...item, result_id: item.result_id ?? id };
    const existing = byId.get(id);
    if (!existing) {
      byId.set(id, normalized);
      return;
    }
    byId.set(id, mergeSafetyRunPair(existing, normalized));
  });
  return sortRunsNewestFirst([...byId.values()]);
}

function normalizeTimestamp(ts: unknown): number {
  if (ts == null) return 0;
  if (typeof ts === 'number' && !Number.isNaN(ts)) return ts;
  if (typeof ts === 'string') {
    const n = Date.parse(ts);
    return Number.isNaN(n) ? 0 : n;
  }
  if (typeof ts === 'object' && ts !== null && '$date' in (ts as Record<string, unknown>)) {
    const d = (ts as { $date?: unknown }).$date;
    if (typeof d === 'number') return d;
    if (typeof d === 'string') {
      const n = Date.parse(d);
      return Number.isNaN(n) ? 0 : n;
    }
  }
  return 0;
}

export function sortRunsNewestFirst(runs: AISafetyRun[]): AISafetyRun[] {
  return [...runs].sort((a, b) => normalizeTimestamp(b.timestamp) - normalizeTimestamp(a.timestamp));
}

/** New AI Safety schema exposes risk_score / score_breakdown. */
export function isNewSafetySchema(run: AISafetyRun | null | undefined): boolean {
  if (!run) return false;
  return run.risk_score != null || run.score_breakdown != null || run.evidence != null;
}

export const LLM_CONTROL_KEYS = ['prompt_abuse', 'harm_detection', 'content_safety', 'leakage'] as const;
export const ML_CONTROL_KEYS = [
  'unsafe_decision_output',
  'protected_class_harm',
  'regulated_decisioning',
  'model_misuse',
  'ml_safety_drift'
] as const;

export const LLM_CONTROL_LABELS: Record<(typeof LLM_CONTROL_KEYS)[number], string> = {
  prompt_abuse: 'Prompt Abuse Risk',
  harm_detection: 'Harm Detection Risk',
  content_safety: 'Content Safety Risk',
  leakage: 'Data Leakage Risk'
};

export const ML_CONTROL_LABELS: Record<(typeof ML_CONTROL_KEYS)[number], string> = {
  unsafe_decision_output: 'Unsafe Decision Output',
  protected_class_harm: 'Protected Class Harm',
  regulated_decisioning: 'Regulated Decisioning',
  model_misuse: 'Model Misuse Risk',
  ml_safety_drift: 'ML Safety Drift'
};

export function readControls(run: AISafetyRun | null | undefined): Record<string, Record<string, unknown>> {
  if (!run) return {};
  const breakdown = run.score_breakdown;
  if (breakdown && typeof breakdown === 'object' && !Array.isArray(breakdown)) {
    const controls = (breakdown as Record<string, unknown>).controls;
    if (controls && typeof controls === 'object' && !Array.isArray(controls)) {
      return controls as Record<string, Record<string, unknown>>;
    }
  }
  return {};
}

export function resolveInputType(run: AISafetyRun | null | undefined): AISafetyInputType {
  if (!run) return 'UNKNOWN';
  const raw = String(run.input_type ?? run.inputType ?? '')
    .trim()
    .toUpperCase();
  if (raw === 'LLM_APP' || raw === 'LLM') return 'LLM_APP';
  if (raw === 'ML_APP' || raw === 'ML') return 'ML_APP';
  if (raw === 'BOTH' || raw === 'HYBRID') return 'BOTH';

  if (isNewSafetySchema(run)) {
    const controls = readControls(run);
    const keys = Object.keys(controls);
    const hasLlm = keys.some(k => k.startsWith('llm_') || (LLM_CONTROL_KEYS as readonly string[]).includes(k));
    const hasMl = keys.some(k => k.startsWith('ml_') || (ML_CONTROL_KEYS as readonly string[]).includes(k));
    if (hasLlm && hasMl) return 'BOTH';
    if (hasMl) return 'ML_APP';
    if (hasLlm) return 'LLM_APP';
  }

  if (run.LLM_ABUSE_ANALYSIS || run.llm_abuse_analysis) {
    if (run.ML_ABUSE_DETECTION || run.ML_Abuse_Detection) return 'BOTH';
    return 'LLM_APP';
  }
  if (run.ML_ABUSE_DETECTION || run.ML_Abuse_Detection) return 'ML_APP';
  return 'LEGACY';
}

function normalizeControlToken(value: string): string {
  return value.trim().toLowerCase().replace(/\s+/g, '_');
}

export function isMlControlKey(key: string): boolean {
  const normalized = normalizeControlToken(key);
  if (normalized.startsWith('ml_')) return true;
  if (normalized.startsWith('llm_')) return false;
  const base = normalized.replace(/^ml_/, '');
  return (ML_CONTROL_KEYS as readonly string[]).includes(base);
}

export function isLlmControlKey(key: string): boolean {
  const normalized = normalizeControlToken(key);
  if (normalized.startsWith('llm_')) return true;
  if (normalized.startsWith('ml_')) return false;
  return (LLM_CONTROL_KEYS as readonly string[]).includes(normalized);
}

export function runHasMlSafetyData(run: AISafetyRun | null | undefined): boolean {
  if (!run) return false;
  const ml = run.ML_ABUSE_DETECTION ?? run.ML_Abuse_Detection;
  if (Array.isArray(ml) && ml.length > 0) return true;

  const inputType = String(run.input_type ?? run.inputType ?? '')
    .trim()
    .toUpperCase();
  if (inputType === 'ML_APP' || inputType === 'ML') return true;
  if (inputType === 'BOTH' || inputType === 'HYBRID') return true;

  if (isNewSafetySchema(run)) {
    const keys = Object.keys(readControls(run));
    return keys.some(k => isMlControlKey(k));
  }

  return false;
}

export function runHasLlmSafetyData(run: AISafetyRun | null | undefined): boolean {
  if (!run) return false;
  if (run.LLM_ABUSE_ANALYSIS || run.llm_abuse_analysis) return true;

  const inputType = String(run.input_type ?? run.inputType ?? '')
    .trim()
    .toUpperCase();
  if (inputType === 'LLM_APP' || inputType === 'LLM') return true;
  if (inputType === 'BOTH' || inputType === 'HYBRID') return true;

  if (isNewSafetySchema(run)) {
    const keys = Object.keys(readControls(run));
    return keys.some(k => k.startsWith('llm_') || (LLM_CONTROL_KEYS as readonly string[]).includes(k));
  }

  return false;
}

export function readDeclaredInputType(run: AISafetyRun | null | undefined): string {
  return String(run?.input_type ?? run?.inputType ?? '')
    .trim()
    .toUpperCase();
}

function normalizeAnalysisTypeToken(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');
}

export function readDeclaredAnalysisType(run: AISafetyRun | null | undefined): string {
  const raw = String(run?.analysis_type ?? run?.analysisType ?? run?.AnalysisType ?? '');
  return normalizeAnalysisTypeToken(raw);
}

function matchesLlmAisafetyType(analysisType: string): boolean {
  if (!analysisType) return false;
  return analysisType === 'llm_aisafety' || analysisType === 'llm_ai_safety' || analysisType.endsWith('_llm_aisafety');
}

function matchesMlAisafetyType(analysisType: string): boolean {
  if (!analysisType) return false;
  return analysisType === 'ml_aisafety' || analysisType === 'ml_ai_safety' || analysisType.endsWith('_ml_aisafety');
}

export function isLlmAisafetyRun(run: AISafetyRun | null | undefined): boolean {
  return matchesLlmAisafetyType(readDeclaredAnalysisType(run));
}

export function isMlAisafetyRun(run: AISafetyRun | null | undefined): boolean {
  return matchesMlAisafetyType(readDeclaredAnalysisType(run));
}

export function isStrictLlmAppRun(run: AISafetyRun | null | undefined): boolean {
  const raw = readDeclaredInputType(run);
  return raw === 'LLM_APP' || raw === 'LLM';
}

export function isStrictMlAppRun(run: AISafetyRun | null | undefined): boolean {
  const raw = readDeclaredInputType(run);
  return raw === 'ML_APP' || raw === 'ML';
}

export function readMlPrefixedControlKeys(run: AISafetyRun | null | undefined): string[] {
  return Object.keys(readControls(run)).filter(key => key.startsWith('ml_'));
}

export function runHasMlPrefixedControls(run: AISafetyRun | null | undefined): boolean {
  return readMlPrefixedControlKeys(run).length > 0;
}

export function readMlPrefixedControls(run: AISafetyRun | null | undefined): Record<string, Record<string, unknown>> {
  const controls = readControls(run);
  return Object.fromEntries(Object.entries(controls).filter(([key]) => key.startsWith('ml_')));
}

export function resolveMlPrefixedControlEntry(
  controls: Record<string, Record<string, unknown>>,
  baseKey: (typeof ML_CONTROL_KEYS)[number]
): Record<string, unknown> | undefined {
  return controls[`ml_${baseKey}`];
}

export function runBelongsToSafetyLane(run: AISafetyRun | null | undefined, lane: 'ml' | 'llm'): boolean {
  if (!run) return false;

  const analysisType = readDeclaredAnalysisType(run);
  if (matchesLlmAisafetyType(analysisType)) return lane === 'llm';
  if (matchesMlAisafetyType(analysisType)) return lane === 'ml';

  const declared = readDeclaredInputType(run);
  if (declared === 'LLM_APP' || declared === 'LLM') return lane === 'llm';
  if (declared === 'ML_APP' || declared === 'ML') return lane === 'ml';
  if (declared === 'BOTH' || declared === 'HYBRID') return true;

  const hasLlm = runHasLlmSafetyData(run);
  const hasMl = runHasMlSafetyData(run);
  if (hasLlm && !hasMl) return lane === 'llm';
  if (hasMl && !hasLlm) return lane === 'ml';
  if (hasLlm && hasMl) return declared === 'BOTH' || declared === 'HYBRID';

  const inferred = resolveInputType(run);
  if (inferred === 'LLM_APP') return lane === 'llm';
  if (inferred === 'ML_APP') return lane === 'ml';
  if (inferred === 'BOTH') return true;

  return false;
}

function formatSafetyVersionDisplayTimestamp(ts: unknown): string {
  const raw = formatSafetyVersionTimestamp(ts);
  if (!raw) return '';
  try {
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return raw.slice(0, 16);
    return d.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return raw.slice(0, 16);
  }
}

function isGenericSafetyTitle(value: string): boolean {
  const t = value.trim().toLowerCase();
  return (
    t === '' ||
    t === 'ml' ||
    t === 'llm' ||
    t === 'llm fairness' ||
    t === 'ml fairness' ||
    t === 'ai safety' ||
    t === 'fairness'
  );
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function readSafetyEntryTitle(entry: Record<string, unknown>): string {
  for (const key of ['title', 'upload_title', 'model_title', 'file_name', 'tag', 'name']) {
    const value = entry[key];
    if (typeof value === 'string' && value.trim() && !isGenericSafetyTitle(value)) {
      return value.trim();
    }
  }
  return '';
}

function resolveSafetyEntryDisplayTitle(
  entry: Record<string, unknown> | undefined,
  run: AISafetyRun,
  lane?: 'ml' | 'llm'
): string {
  const analysisType = readDeclaredAnalysisType(run);
  const entryTitle = entry ? readSafetyEntryTitle(entry) : '';
  if (entryTitle && !safetyTitleContradictsLane(entryTitle, lane, analysisType)) {
    return entryTitle;
  }

  const runCandidates = [run.title, run.model_title, run.upload_title, run.file_name, run.name]
    .map(value => String(value ?? '').trim())
    .filter(Boolean);

  for (const candidate of runCandidates) {
    if (isGenericSafetyTitle(candidate)) continue;
    if (safetyTitleContradictsLane(candidate, lane, analysisType)) continue;
    return candidate;
  }

  return '';
}

function isSafetyEntryVersionRecord(entry: Record<string, unknown>): boolean {
  if (readSafetyEntryTitle(entry)) return true;
  if (entry.result_id != null || entry._id != null) return true;
  if (isNewSafetySchema(entry as AISafetyRun)) return true;
  const nested = entry.data ?? entry.entries;
  return Array.isArray(nested) && nested.length > 0;
}

function shouldExpandSafetyEntries(rawEntries: Record<string, unknown>[]): boolean {
  if (rawEntries.length <= 1) return false;
  return rawEntries.some(entry => readSafetyEntryTitle(entry) || isSafetyEntryVersionRecord(entry));
}

function laneEntriesForSafetyRun(run: AISafetyRun): Record<string, unknown>[] {
  const analysisType = readDeclaredAnalysisType(run);
  const llmEntries = getArray(run.llm_entries).filter(isRecord);
  const mlEntries = getArray(run.ml_entries).filter(isRecord);

  if (matchesLlmAisafetyType(analysisType)) return llmEntries;
  if (matchesMlAisafetyType(analysisType)) return mlEntries;
  if (llmEntries.length > 0 && mlEntries.length === 0) return llmEntries;
  if (mlEntries.length > 0 && llmEntries.length === 0) return mlEntries;
  return [];
}

function mergeSafetyRunWithEntry(
  run: AISafetyRun,
  entry: Record<string, unknown> | undefined,
  index: number,
  lane?: 'ml' | 'llm',
  forceUniqueId = false
): AISafetyRun {
  const analysisType = readDeclaredAnalysisType(run);
  const resolvedLane: 'ml' | 'llm' | undefined =
    lane ?? (matchesLlmAisafetyType(analysisType) ? 'llm' : matchesMlAisafetyType(analysisType) ? 'ml' : undefined);
  const title =
    (entry ? resolveSafetyEntryDisplayTitle(entry, run, resolvedLane) : '') ||
    resolveSafetyEntryDisplayTitle(undefined, run, resolvedLane) ||
    resolveSafetyVersionBaseLabel(run, index, resolvedLane);

  const parentId = String(run.result_id ?? '') || `${String(run.title ?? 'safety')}-${String(run.timestamp ?? index)}`;
  const entryId = String(entry?.result_id ?? entry?._id ?? '').trim();
  const result_id =
    entryId && !forceUniqueId ? entryId : forceUniqueId ? `${parentId}-entry-${index}` : entryId || parentId;

  const timestamp = entry?.timestamp ?? run.timestamp;
  const entryHasPayload = entry != null && isNewSafetySchema(entry as AISafetyRun);

  if (entryHasPayload) {
    return {
      ...run,
      ...entry,
      title,
      result_id,
      timestamp,
      analysis_type: run.analysis_type ?? entry.analysis_type
    };
  }

  return {
    ...run,
    ...(entry ?? {}),
    title,
    result_id,
    timestamp
  };
}

function expandSingleSafetyRunByEntries(run: AISafetyRun): AISafetyRun[] {
  const analysisType = readDeclaredAnalysisType(run);
  const lane: 'ml' | 'llm' | undefined = matchesLlmAisafetyType(analysisType)
    ? 'llm'
    : matchesMlAisafetyType(analysisType)
    ? 'ml'
    : undefined;
  const laneEntries = laneEntriesForSafetyRun(run);
  if (laneEntries.length === 0) return [run];

  if (shouldExpandSafetyEntries(laneEntries)) {
    return laneEntries.map((entry, index) => mergeSafetyRunWithEntry(run, entry, index, lane, true));
  }

  const titledEntry = laneEntries.find(entry => readSafetyEntryTitle(entry)) ?? laneEntries[0];
  return [mergeSafetyRunWithEntry(run, titledEntry, 0, lane)];
}

/** Expand ai_safety runs that carry per-upload rows in llm_entries / ml_entries. */
export function expandSafetyRunsByEntries(runs: AISafetyRun[]): AISafetyRun[] {
  const expanded = runs.flatMap(run => expandSingleSafetyRunByEntries(run));
  return sortRunsNewestFirst(expanded);
}

function safetyTitleContradictsLane(title: string, lane: 'ml' | 'llm' | undefined, analysisType: string): boolean {
  const token = title.trim().toLowerCase();
  if (matchesLlmAisafetyType(analysisType) || lane === 'llm') return token === 'ml';
  if (matchesMlAisafetyType(analysisType) || lane === 'ml') return token === 'llm';
  return false;
}

export type SafetyUploadCatalogEntry = {
  uploadId: string | null;
  title: string;
  kind: 'ml' | 'llm';
  fileKey: string | null;
  baseTitle: string;
};

const UPLOAD_UUID_REGEX = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;

function asUploadArray(value: unknown): Record<string, unknown>[] {
  if (Array.isArray(value)) {
    return value.filter((item): item is Record<string, unknown> => Boolean(item) && typeof item === 'object');
  }
  if (value && typeof value === 'object') return [value as Record<string, unknown>];
  return [];
}

function readUploadRecordTitle(record: Record<string, unknown>): string {
  return String(record.title ?? record.tag ?? record.file_name ?? record.name ?? '').trim();
}

function readUploadFileKey(record: Record<string, unknown>): string | null {
  const meta = record.model_metadata;
  const metaRow = meta && typeof meta === 'object' ? (meta as Record<string, unknown>) : null;
  for (const candidate of [record.file_url, record.file_name, metaRow?.file_url, metaRow?.file_name]) {
    if (typeof candidate === 'string' && candidate.trim() && candidate !== 'api_direct_submission') {
      return candidate.trim().toLowerCase();
    }
  }
  return null;
}

/** Index project uploads so version history can show ML / LLM file titles. */
export function buildSafetyUploadCatalog(uploads: unknown): SafetyUploadCatalogEntry[] {
  if (!uploads || typeof uploads !== 'object') return [];
  const root = uploads as Record<string, unknown>;
  const entries: SafetyUploadCatalogEntry[] = [];
  const seen = new Set<string>();

  const push = (record: Record<string, unknown>, kind: 'ml' | 'llm') => {
    const title = readUploadRecordTitle(record);
    if (!title) return;
    const uploadId = resolveUploadRecordId(record);
    const fileKey = readUploadFileKey(record);
    const baseTitle = normalizeUploadBaseTitle(title);
    const dedupeKey = uploadId ? `id:${uploadId}` : fileKey ? `file:${fileKey}` : baseTitle ? `title:${baseTitle}` : '';
    if (dedupeKey && seen.has(dedupeKey)) return;
    if (dedupeKey) seen.add(dedupeKey);
    entries.push({ uploadId, title, kind, fileKey, baseTitle });
  };

  asUploadArray(root.ml_model).forEach(record => push(record, 'ml'));
  asUploadArray(root.ml_models).forEach(record => push(record, 'ml'));
  [...asUploadArray(root.llm_models), ...asUploadArray(root.llm_model), ...asUploadArray(root.LLM_MODEL)].forEach(
    record => push(record, 'llm')
  );

  return entries;
}

function collectRunUploadMatchTokens(run: AISafetyRun): string[] {
  const tokens = new Set<string>();
  const add = (value: unknown) => {
    if (value == null) return;
    const raw = String(value).trim();
    if (!raw) return;
    tokens.add(raw.toLowerCase());
    const base = normalizeUploadBaseTitle(raw);
    if (base) tokens.add(base);
    for (const match of raw.matchAll(UPLOAD_UUID_REGEX)) {
      tokens.add(match[0].toLowerCase());
    }
  };

  add(run.upload_id);
  add(run.uploadId);
  add(run.title);
  add(run.model_title);
  add(run.upload_title);
  add(run.file_name);
  add(run.fileName);
  add(run.tag);

  const meta = run.model_metadata;
  if (meta && typeof meta === 'object') {
    const metaRow = meta as Record<string, unknown>;
    add(metaRow.file_name);
    add(metaRow.file_url);
  }

  const walk = (node: unknown, depth = 0) => {
    if (node == null || depth > 4) return;
    if (Array.isArray(node)) {
      node.forEach(item => walk(item, depth + 1));
      return;
    }
    if (typeof node !== 'object') return;
    const row = node as Record<string, unknown>;
    add(row.upload_id);
    add(row.uploadId);
    add(row.title);
    add(row.tag);
    add(row.file_name);
    add(row.file_url);
    walk(row.data, depth + 1);
    walk(row.entries, depth + 1);
  };

  walk(run.data);
  walk(run.entries);
  walk(run.llm_entries);
  walk(run.ml_entries);

  return [...tokens];
}

export function resolveSafetyUploadTitle(
  run: AISafetyRun,
  catalog: SafetyUploadCatalogEntry[],
  lane?: 'ml' | 'llm'
): string | null {
  if (!catalog.length) return null;

  const laneCatalog = lane ? catalog.filter(entry => entry.kind === lane) : catalog;
  if (!laneCatalog.length) return null;

  const tokens = collectRunUploadMatchTokens(run);
  if (!tokens.length) return null;
  const tokenSet = new Set(tokens);

  for (const entry of laneCatalog) {
    if (entry.uploadId && tokenSet.has(entry.uploadId)) return entry.title;
    if (entry.fileKey && tokenSet.has(entry.fileKey)) return entry.title;
    if (entry.baseTitle && tokenSet.has(entry.baseTitle)) return entry.title;
  }

  for (const token of tokens) {
    const match = laneCatalog.find(entry => normalizeUploadBaseTitle(entry.title) === token);
    if (match) return match.title;
  }

  if (laneCatalog.length === 1) return laneCatalog[0].title;

  return null;
}

/** Map each run to its upload title; pairs unmatched runs to lane uploads by run order. */
export function resolveSafetyRunUploadTitleMap(
  runs: AISafetyRun[],
  catalog: SafetyUploadCatalogEntry[],
  lane: 'ml' | 'llm'
): Map<string, string> {
  const out = new Map<string, string>();
  const laneCatalog = catalog.filter(entry => entry.kind === lane);
  const usedCatalog = new Set<number>();

  runs.forEach((run, index) => {
    const runId = createVersionId(run, index);
    const direct = resolveSafetyUploadTitle(run, catalog, lane);
    if (!direct) return;
    out.set(runId, direct);
    const catIdx = laneCatalog.findIndex(
      entry => entry.title === direct || normalizeUploadBaseTitle(entry.title) === normalizeUploadBaseTitle(direct)
    );
    if (catIdx >= 0) usedCatalog.add(catIdx);
  });

  const unmatchedRuns = runs.filter((run, index) => !out.has(createVersionId(run, index)));
  const unusedCatalog = laneCatalog.filter((_, index) => !usedCatalog.has(index));

  if (unmatchedRuns.length > 0 && unusedCatalog.length > 0) {
    const chronologicalRuns = [...unmatchedRuns].sort(
      (a, b) => normalizeTimestamp(a.timestamp) - normalizeTimestamp(b.timestamp)
    );
    chronologicalRuns.forEach((run, pairIndex) => {
      const catalogEntry = unusedCatalog[Math.min(pairIndex, unusedCatalog.length - 1)];
      if (!catalogEntry) return;
      const runIndex = runs.indexOf(run);
      out.set(createVersionId(run, runIndex >= 0 ? runIndex : 0), catalogEntry.title);
    });
  }

  return out;
}

/** Version label from upload name, then meaningful run fields, then timestamp. */
export function resolveSafetyVersionBaseLabel(
  run: AISafetyRun,
  fallbackIndex: number,
  lane?: 'ml' | 'llm',
  uploadCatalog?: SafetyUploadCatalogEntry[],
  uploadTitleOverride?: string
): string {
  const analysisType = readDeclaredAnalysisType(run);

  if (uploadTitleOverride && !isGenericSafetyTitle(uploadTitleOverride)) {
    return uploadTitleOverride;
  }

  const laneEntries = laneEntriesForSafetyRun(run);
  if (laneEntries.length > 0) {
    const titledEntry = laneEntries.find(entry => readSafetyEntryTitle(entry)) ?? laneEntries[0];
    const entryTitle = resolveSafetyEntryDisplayTitle(titledEntry, run, lane);
    if (entryTitle) return entryTitle;
  }

  const uploadTitle = uploadCatalog?.length ? resolveSafetyUploadTitle(run, uploadCatalog, lane) : null;
  if (
    uploadTitle &&
    !isGenericSafetyTitle(uploadTitle) &&
    !safetyTitleContradictsLane(uploadTitle, lane, analysisType)
  ) {
    return uploadTitle;
  }

  const candidates = [run.title, run.model_title, run.upload_title, run.name]
    .map(value => String(value ?? '').trim())
    .filter(Boolean);

  for (const candidate of candidates) {
    if (isGenericSafetyTitle(candidate)) continue;
    if (safetyTitleContradictsLane(candidate, lane, analysisType)) continue;
    return candidate;
  }

  const tsLabel = formatSafetyVersionDisplayTimestamp(run.timestamp);
  if (tsLabel) return tsLabel;

  if (matchesLlmAisafetyType(analysisType) || lane === 'llm') return 'LLM';
  if (matchesMlAisafetyType(analysisType) || lane === 'ml') return 'ML';

  return `Run ${fallbackIndex + 1}`;
}

export function formatSafetyVersionTitle(
  run: AISafetyRun,
  fallbackIndex: number,
  lane?: 'ml' | 'llm',
  uploadCatalog?: SafetyUploadCatalogEntry[],
  uploadTitleOverride?: string
): string {
  return resolveSafetyVersionBaseLabel(run, fallbackIndex, lane, uploadCatalog, uploadTitleOverride);
}

export function filterRunsBySafetyLane(runs: AISafetyRun[], lane: 'ml' | 'llm'): AISafetyRun[] {
  return runs.filter(run => runBelongsToSafetyLane(run, lane));
}

export function matchesSafetyRunId(run: AISafetyRun, id: string): boolean {
  if (!id) return false;
  const rid = String(run.result_id ?? '');
  if (rid && rid === id) return true;
  return createVersionId(run, 0) === id;
}

export function matchesSafetyControlLane(controlName: string, lane: 'ml' | 'llm'): boolean {
  const normalized = normalizeControlToken(controlName);
  if (lane === 'ml') {
    return normalized.startsWith('ml_') || isMlControlKey(normalized);
  }
  if (isLlmControlKey(normalized)) return true;
  return Object.values(LLM_CONTROL_LABELS).some(label => normalizeControlToken(label) === normalized);
}

export function filterControlEntriesByLane(
  controls: Record<string, Record<string, unknown>>,
  lane: 'ml' | 'llm'
): Record<string, Record<string, unknown>> {
  return Object.fromEntries(
    Object.entries(controls).filter(([key]) => (lane === 'ml' ? key.startsWith('ml_') : isLlmControlKey(key)))
  );
}

export function controlScore(entry: Record<string, unknown> | undefined): number | null {
  if (!entry) return null;
  const raw = entry.score ?? entry.weighted_sum ?? entry.Score ?? entry.Weighted_Sum;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

export function resolveControlEntry(
  controls: Record<string, Record<string, unknown>>,
  key: string,
  prefix?: 'llm' | 'ml'
): Record<string, unknown> | undefined {
  if (prefix === 'ml') {
    return controls[`ml_${key}`] ?? controls[key];
  }
  if (controls[key]) return controls[key];
  if (prefix) {
    const prefixed = `${prefix}_${key}`;
    if (controls[prefixed]) return controls[prefixed];
  }
  return undefined;
}

export function formatSafetyVersionTimestamp(ts: unknown): string {
  if (ts == null || ts === '') return '';
  if (typeof ts === 'number' && !Number.isNaN(ts)) {
    try {
      return new Date(ts).toISOString();
    } catch {
      return String(ts);
    }
  }
  if (typeof ts === 'string') return ts.trim();
  if (typeof ts === 'object' && ts !== null && '$date' in (ts as Record<string, unknown>)) {
    const d = (ts as { $date?: unknown }).$date;
    if (typeof d === 'number') return new Date(d).toISOString();
    if (typeof d === 'string') return d;
  }
  return '';
}

export function createVersionId(item: AISafetyRun, fallbackIndex: number): string {
  return String(item.result_id ?? '') || `${String(item.title ?? 'safety')}-${String(item.timestamp ?? fallbackIndex)}`;
}

export function pickLatestSafetyRun(projectResults: unknown): AISafetyRun | null {
  if (!projectResults || typeof projectResults !== 'object') return null;
  const pr = projectResults as Record<string, unknown>;
  for (const rootKey of ['collection_results', 'results', 'feature_results'] as const) {
    const root = pr[rootKey];
    if (!root || typeof root !== 'object') continue;
    const bucket = root as Record<string, unknown>;
    if (bucket.ai_safety != null) {
      return pickLatestCollectionRun(bucket.ai_safety as AISafetyRun[]) as AISafetyRun | null;
    }
  }
  for (const rootKey of ['collection_results', 'results', 'feature_results'] as const) {
    const root = pr[rootKey];
    if (!root || typeof root !== 'object') continue;
    const bucket = root as Record<string, unknown>;
    if (bucket.abuse_ai != null) {
      return pickLatestCollectionRun(bucket.abuse_ai as AISafetyRun[]) as AISafetyRun | null;
    }
  }
  return null;
}

export function resolveSafetyDataFromResults(results: Record<string, unknown> | null | undefined): unknown {
  if (!results) return undefined;
  return results.ai_safety ?? results.abuse_ai;
}
